import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Sidebar from './Sidebar';
import Topbar from './Topbar';

const Shift44CardDetails = () => {
  // Helper functions for Pacific timezone date calculations
  const getTodayInPacific = () => {
    const now = new Date();
    const pacificTime = new Date(now.toLocaleString("en-US", {timeZone: "America/Los_Angeles"}));
    const year = pacificTime.getFullYear();
    const month = String(pacificTime.getMonth() + 1).padStart(2, '0');
    const day = String(pacificTime.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getYesterdayInPacific = () => {
    const now = new Date();
    const pacificTime = new Date(now.toLocaleString("en-US", {timeZone: "America/Los_Angeles"}));
    pacificTime.setDate(pacificTime.getDate() - 1);
    const year = pacificTime.getFullYear();
    const month = String(pacificTime.getMonth() + 1).padStart(2, '0');
    const day = String(pacificTime.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const [revenueData, setRevenueData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchingApi, setFetchingApi] = useState(false);
  const [error, setError] = useState('');
  const [fetchMessage, setFetchMessage] = useState('');
  const [user, setUser] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(getTodayInPacific()); // Default to today's date (dynamic)
  const navigate = useNavigate();

  const fetchRevenueData = useCallback(async (date = selectedDate) => {
    setLoading(true);
    setError('');
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/revenue-data', {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        params: {
          source: 'Shift44'
        }
      });
      
      console.log('Raw data from API:', response.data.slice(0, 3)); // Debug log
      console.log('Looking for date:', date); // Debug log
      
      // Filter data by the selected date on the frontend
      const filteredData = response.data.filter(item => {
        // Simply extract the date part from the ISO string - avoids timezone issues
        const itemDateString = item.date.split('T')[0];
        
        console.log(`Comparing: ${itemDateString} === ${date} (item raw: ${item.date})`); // Debug log
        return itemDateString === date;
      });
      
      console.log(`Filtered ${filteredData.length} records for date ${date}`); // Debug log
      setRevenueData(filteredData);
      
    } catch (error) {
      setError('Failed to fetch revenue data');
      console.error('Error fetching revenue data:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);

  const triggerApiFetch = async () => {
    setFetchingApi(true);
    setFetchMessage('');
    setError('');
    
    try {
      const token = localStorage.getItem('token');
      
      // Call the API to fetch data for the selected date
      console.log(`Triggering API fetch for date: ${selectedDate}`);
      
      await axios.post('/api/fetch-data/shift44', {
        targetDate: selectedDate // Send the selected date to the API
      }, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      setFetchMessage(`Successfully fetched data for ${selectedDate}`);
      
      // Wait a moment then refresh the displayed data
      setTimeout(() => {
        fetchRevenueData(selectedDate);
      }, 2000);
      
    } catch (error) {
      console.error('Error triggering API fetch:', error);
      const errorMsg = error.response?.data?.message || 'Failed to fetch API data';
      setFetchMessage(`${errorMsg}`);
    } finally {
      setFetchingApi(false);
    }
  };

  useEffect(() => {
    fetchRevenueData();
    
    // Get user data from localStorage
    const userData = localStorage.getItem('user');
    if (userData) {
      setUser(JSON.parse(userData));
    }
  }, [selectedDate, fetchRevenueData]);

  const handleDateChange = (event) => {
    const newDate = event.target.value;
    setSelectedDate(newDate);
    setFetchMessage(''); // Clear any previous fetch messages when date changes
  };

  const formatDateDisplay = (dateString) => {
    // Parse the date string properly to avoid timezone issues
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day); // month is 0-indexed in JavaScript
    
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getDateLabel = (dateString) => {
    const today = getTodayInPacific();
    const yesterday = getYesterdayInPacific();
    
    if (dateString === today) {
      return 'Today';
    } else if (dateString === yesterday) {
      return 'Yesterday';
    } else {
      return formatDateDisplay(dateString);
    }
  };

  const calculateTotalRevenue = () => {
    return revenueData.reduce((total, item) => total + item.revenue, 0).toFixed(2);
  };

  const getOfferSidBreakdown = () => {
    const offers = {};
    
    console.log('Shift44CardDetails - Raw revenue data:', revenueData);
    
    revenueData.forEach(item => {
      const offerName = item.offer || 'Unknown Offer';
      const sid = item.s1 || 'N/A';
      
      console.log(`Processing item: Offer ${offerName}, SID: ${sid}, Revenue: ${item.revenue}`);
      
      if (!offers[offerName]) {
        offers[offerName] = {
          name: offerName,
          totalRevenue: 0,
          sidBreakdown: {}
        };
      }
      
      if (!offers[offerName].sidBreakdown[sid]) {
        offers[offerName].sidBreakdown[sid] = 0;
      }
      
      offers[offerName].sidBreakdown[sid] += item.revenue;
      offers[offerName].totalRevenue += item.revenue;
    });
    
    // Sort SID breakdown within each offer by revenue
    Object.keys(offers).forEach(offerName => {
      const sidEntries = Object.entries(offers[offerName].sidBreakdown);
      offers[offerName].sidBreakdown = Object.fromEntries(
        sidEntries.sort(([, a], [, b]) => b - a)
      );
    });
    
    console.log('Shift44CardDetails - Final offers breakdown:', offers);
    return offers;
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  if (loading) {
    return (
      <div className="dashboard-container">
        <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
        
        <div className="main-content">
          <Topbar 
            user={user} 
            onLogout={handleLogout} 
            onToggleSidebar={toggleSidebar}
          />
          
          <div className="content-wrapper">
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2 text-muted">Loading revenue data...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      
      <div className="main-content">
        <Topbar 
          user={user} 
          onLogout={handleLogout} 
          onToggleSidebar={toggleSidebar}
        />
        
        <div className="content-wrapper">
          {/* Back to Dashboard button - mobile version */}
          <div className="d-block d-md-none mb-3">
            <button 
              className="btn btn-dark btn-sm back-to-dashboard-mobile"
              onClick={() => navigate('/dashboard')}
            >
              ← Back to Dashboard
            </button>
          </div>

          {/* Back to Dashboard button - desktop version, right under topbar */}
          <div className="d-none d-md-block mb-3">
            <div className="d-flex justify-content-end">
              <button 
                className="btn btn-dark"
                onClick={() => navigate('/dashboard')}
                style={{
                  backgroundColor: '#000',
                  borderColor: '#000',
                  color: '#fff'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#0d6efd';
                  e.target.style.borderColor = '#0d6efd';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#000';
                  e.target.style.borderColor = '#000';
                }}
              >
                ← Back to Dashboard
              </button>
            </div>
          </div>

          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h1 className="h3 mb-0 text-gray-800">Shift 44 SID Revenue Report</h1>
              <p className="text-muted mb-0">All Conversions</p>
              {/* Current date/time info */}
              <small className="text-info">
                Pacific Time - Today: {getTodayInPacific()}, Yesterday: {getYesterdayInPacific()}, Selected: {selectedDate}
              </small>
              <br />
              <small className="text-muted">
                Current Pacific Time: {new Date().toLocaleString("en-US", {timeZone: "America/Los_Angeles"})}
              </small>
            </div>
          </div>

          {error && (
            <div className="alert alert-warning mb-4">
              {error}
            </div>
          )}

          {/* Date Picker - Always visible */}
          <div className="row mb-4">
            <div className="col-12 col-md-8 offset-md-2">
              <div className="dashboard-card">
                <div className="card-header">
                  <h5 className="card-title">Select Date & Run Report</h5>
                </div>
                <div className="card-content">
                  <div className="mb-3">
                    <small className="text-muted">
                      Select any date to view or fetch data for that full day
                    </small>
                    <label htmlFor="datePicker" className="form-label small text-muted">
                      Select Any Date:
                    </label>
                    <div className="d-flex align-items-center gap-2">
                      <input
                        type="date"
                        id="datePicker"
                        className="form-control form-control-sm"
                        style={{ maxWidth: '200px' }}
                        value={selectedDate}
                        onChange={handleDateChange}
                      />
                      <button
                        className="btn btn-primary btn-lg"
                        onClick={triggerApiFetch}
                        disabled={fetchingApi || loading}
                        title="Fetch fresh data from API for selected date"
                        style={{ whiteSpace: 'nowrap', minWidth: '140px' }}
                      >
                        {fetchingApi ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                            Fetching...
                          </>
                        ) : (
                          'Run Report'
                        )}
                      </button>
                    </div>
                  
                    <div className={`small mt-2 ${fetchMessage.includes('Successfully') ? 'text-success' : 'text-danger'}`}>
                      {fetchMessage.includes('Successfully') ? (
                        <><i className="fa-solid fa-check me-1"></i>{fetchMessage}</>
                      ) : (
                        <><i className="fa-solid fa-times me-1"></i>{fetchMessage}</>
                      )}
                    </div>
    
                    <div className="mt-3 text-muted">
                      <strong>Data for:</strong> {getDateLabel(selectedDate)}
                      {selectedDate !== getDateLabel(selectedDate) && (
                        <span className="text-muted ms-2">({formatDateDisplay(selectedDate)})</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {revenueData.length > 0 ? (
            <div className="row">
              {/* Offer SID Breakdown */}
              <div className="col-12 col-md-8 offset-md-2">
                {(() => {
                  const offerBreakdown = getOfferSidBreakdown();
                  console.log('Rendering offers:', Object.keys(offerBreakdown));
                  return Object.entries(offerBreakdown).map(([offerName, offer]) => (
                    <div className="dashboard-card mb-4" key={offerName}>
                      <div className="card-header">
                        <div className="text-muted mb-3">
                          <div className="fs-6 mb-2">
                            <h5 className="card-title">Offer: {offer.name} - SID Revenue Breakdown</h5>
                            <div className="text-muted">
                              <h5>Offer Total: <strong className="text-success">${offer.totalRevenue.toFixed(2)}</strong></h5>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="card-content">
                        <div className="s1-breakdown">
                          {Object.entries(offer.sidBreakdown).map(([sid, revenue]) => (
                            <div key={sid} className="d-flex justify-content-between align-items-center py-3 border-bottom">
                              <span className="fw-medium fs-5">{sid}</span>
                              <span className="text-success fw-bold fs-5">${revenue.toFixed(2)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ));
                })()}
                
                {/* Overall Total */}
                <div className="dashboard-card">
                  <div className="card-header">
                    <h5 className="card-title">Overall Total</h5>
                  </div>
                  <div className="card-content">
                    <div className="text-center py-3">
                      <h3 className="text-success">${calculateTotalRevenue()}</h3>
                      <p className="text-muted">Total Revenue Across All Offers</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="row">
              <div className="col-12 col-md-8 offset-md-2">
                <div className="dashboard-card">
                  <div className="card-content">
                    <div className="text-center py-5">
                      <div className="text-muted">
                        <h4>No revenue data available</h4>
                        <p>No data found for {getDateLabel(selectedDate)} ({selectedDate})</p>
                        <p className="small">
                          Click "Run Report" above to pull fresh data for the selected date
                        </p>
                        <div className="mt-3">
                          <button 
                            className="btn btn-outline-secondary btn-sm"
                            onClick={() => setSelectedDate(getYesterdayInPacific())}
                          >
                            Try Yesterday ({getYesterdayInPacific()})
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Shift44CardDetails; 